import { useCallback, useLayoutEffect, useState } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { tutorialState } from '@/stores/tutorialStore';
import { useTracker } from '@/core/tracking';

export default function useTutorial() {
  const { track } = useTracker();
  const resetTutorial = useResetRecoilState(tutorialState);
  const [tutorial, setTutorial] = useRecoilState(tutorialState);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<DOMRect>();

  const getTutorialStepLayoutPosition = useCallback(
    (position: 'top' | 'bottom' | 'left' | 'right') => {
      if (position === 'top') return '-25%, -110%';
      if (position === 'bottom') return 'translate(-25%, 50%)';
      if (position === 'left') return 'translate(-110%, -25%)';
      if (position === 'right') {
        return `translate(${(anchorPosition?.width || 0) + 20}px, -35%)`;
      }
    },
    [anchorPosition?.width]
  );

  const getPinPosition = (position: 'top' | 'bottom' | 'left' | 'right') => {
    if (position === 'top') {
      return 'bottom-0 left-1/2 translate-y-1/2 -translate-x-2/4 border-l-8 border-r-8 border-transparent border-t-8 border-t-secondary-yellow';
    }
    if (position === 'bottom') {
      return 'top-0 left-1/2 -translate-y-1/2 -translate-x-2/4 border-l-8 border-r-8 border-transparent border-b-8 border-b-secondary-yellow';
    }
    if (position === 'left') {
      return 'top-2/4 right-0 -translate-y-1/2 translate-x-2/4 border-t-8 border-b-8 border-transparent border-l-8 border-l-secondary-yellow';
    }
    if (position === 'right') {
      return 'top-2/4 left-0 -translate-y-1/2 -translate-x-2/4 border-t-8 border-b-8 border-transparent border-r-8 border-r-secondary-yellow';
    }
  };

  const showNextStep = useCallback(
    (currentIndex: number) => {
      if (currentIndex >= tutorial.steps.length) {
        track('tutorial', 'completed', { tutorialId: tutorial.id });
        resetTutorial();
      } else {
        setTutorial((prev) => ({ ...prev, currentStep: currentIndex + 1 }));
      }
    },
    [resetTutorial, setTutorial, track, tutorial.id, tutorial.steps.length]
  );

  const showPrevStep = useCallback(
    (currentIndex: number) => {
      setTutorial((prev) => ({ ...prev, currentStep: currentIndex - 1 }));
    },
    [setTutorial]
  );

  const changeAnchorPosition = useCallback(() => {
    const divElement = document.getElementById(
      `${tutorial.id}-step${tutorial.currentStep}`
    );

    setAnchorElement(divElement);
    setAnchorPosition(divElement?.getBoundingClientRect());
  }, [tutorial.currentStep, tutorial.id]);

  const removeObserver = (observer) => {
    if (observer) {
      observer.disconnect();
    }
  };

  useLayoutEffect(() => {
    const observer = new MutationObserver(() => changeAnchorPosition());
    observer.observe(document, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      removeObserver(observer);
    };
  }, [changeAnchorPosition]);

  return {
    showPrevStep,
    showNextStep,
    tutorial,
    anchorElement,
    anchorPosition,
    resetTutorial,
    getTutorialStepLayoutPosition,
    getPinPosition,
  };
}
